// Hub della serie video "Strumenti derivati" / "Derivatives" (IT/EN).
// Presentazione della playlist + una scheda per ogni episodio PUBBLICATO nella lingua corrente
// (dati in src/data/derivatiEpisodes.js). Le schede sono <details>: il testo resta nel HTML
// pre-renderizzato (indicizzabile) ma la pagina rimane leggibile anche con molti episodi.
// Ancore: /series/derivati#ep-9 apre e porta alla scheda (link dalla Knowledge Base).
import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Seo from "../components/Seo";
import YouTubeEmbed from "../components/YouTubeEmbed";
import { trackVideoPlay } from "../hooks/useAnalytics";
import { BM } from "../components/Maths";
import "katex/dist/katex.min.css";
import { LangContext } from "../lang/LangProvider";
import { DERIVATI_EPISODES, DERIVATI_GROUPS, DERIVATI_PLAYLIST } from "../data/derivatiEpisodes";
import "./Series.css";

function L(lang, it, en) { return lang === "en" ? en : it; }

const KB_READING = [
  { slug: "derivati-concetti-chiave", it: "I passaggi chiave della serie, dal future a Black-Scholes", en: "The key steps of the series, from futures to Black-Scholes" },
  { slug: "derivati-spiegati-semplice", it: "Derivati: cosa sono e come funzionano", en: "Derivatives: what they are and how they work" },
  { slug: "derivati-futures-come-funzionano", it: "Futures, parte 1: come funzionano", en: "Futures, part 1: how they work" },
  { slug: "derivati-futures-coperture-hedge-ratio", it: "Futures, parte 2: coprirsi davvero", en: "Futures, part 2: hedging properly" },
  { slug: "certificati-covered-warrant", it: "Certificati e covered warrant", en: "Certificates and covered warrants" },
];

function EpisodeCard({ ep, c, lang, open, onToggle }) {
  const label = ep.n ? `${L(lang, "Ep.", "Ep.")} ${ep.n}` : L(lang, "Extra", "Extra");
  return (
    <details className="deriv-ep" id={ep.id} open={open} onToggle={onToggle}>
      <summary>
        <span className="deriv-ep-n">{label}</span>
        <span className="deriv-ep-head">
          <span className="deriv-ep-title">{c.title}</span>
          <span className="deriv-ep-sum">{c.summary}</span>
        </span>
      </summary>
      <div className="deriv-ep-body">
        <YouTubeEmbed videoId={c.videoId} title={c.title} onPlay={(id) => trackVideoPlay(id, "derivati_series")} />

        <h3>{L(lang, "Cosa impari", "What you will learn")}</h3>
        <ul>{c.learn.map((x, i) => <li key={i}>{x}</li>)}</ul>

        <h3>{L(lang, "Concetti chiave", "Key concepts")}</h3>
        <ul>{c.concepts.map((x, i) => <li key={i}>{x}</li>)}</ul>

        {c.formula && (
          <div className="deriv-formula"><BM>{c.formula}</BM></div>
        )}

        <h3>{L(lang, "Esempio", "Example")}</h3>
        <p>{c.example}</p>

        <h3>{L(lang, "Errori e rischi da conoscere", "Mistakes and risks to know")}</h3>
        <p>{c.risks}</p>

        <p className="deriv-ref">
          {L(lang, "Riferimento", "Reference")}: {c.ref}
          {c.kb && (
            <>
              {" · "}
              {L(lang, "Da leggere", "Read more")}:{" "}
              <Link to={`/${lang}/kb/${c.kb.slug}`}>{c.kb.label}</Link>
            </>
          )}
        </p>
      </div>
    </details>
  );
}

export default function DerivatiHub() {
  const { lang } = useContext(LangContext);
  const k = lang === "en" ? "en" : "it";
  const { hash } = useLocation();
  const [openId, setOpenId] = useState(null);

  // Episodi pubblicati nella lingua corrente
  const published = DERIVATI_EPISODES.filter((e) => e[k]);
  const numbered = published.filter((e) => e.n).length;

  // Link con ancora (#ep-9): apre la scheda e ci porta sopra. Lo ScrollToTop di App.js gira dopo
  // gli effect dei figli, quindi lo scroll è differito.
  useEffect(() => {
    const id = (hash || "").replace("#", "");
    if (!id || !published.some((e) => e.id === id)) return undefined;
    setOpenId(id);
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ block: "start" });
    }, 80);
    return () => clearTimeout(t);
  }, [hash]); // eslint-disable-line react-hooks/exhaustive-deps

  const seoTitle = L(lang, "Strumenti derivati: la serie video, dalle basi a Black-Scholes", "Derivatives: the video series, from the basics to Black-Scholes");
  const seoDesc = L(
    lang,
    "Serie video educativa sui derivati: futures e coperture, tassi e swap, opzioni e strategie, albero binomiale, processi stocastici e Black-Scholes. Per ogni episodio una scheda con concetti chiave, esempio numerico e rischi.",
    "Educational video series on derivatives: futures and hedging, rates and swaps, options and strategies, binomial trees, stochastic processes and Black-Scholes. A companion note for every episode with key concepts, a worked example and risks."
  );

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={`/${lang}/series/derivati`}
        image="/og/cover-tools.png"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CreativeWorkSeries",
          name: L(lang, "Strumenti derivati", "Derivatives"),
          description: seoDesc,
          inLanguage: lang,
          isAccessibleForFree: true,
          publisher: { "@type": "Organization", name: "AlphaFrame Analytics" },
        }}
      />
      <div className="series-hub">
        <p className="series-eyebrow">
          <Link to={`/${lang}/series`}>← {L(lang, "Le Serie", "Series")}</Link>
        </p>
        <header className="series-head">
          <h1>{L(lang, "Strumenti derivati", "Derivatives")}</h1>
          <p className="series-intro">
            {L(
              lang,
              "Una serie video, in italiano e in inglese, che parte dalle basi e arriva ai modelli di prezzo. Segue l'impianto di «Options, Futures and Other Derivatives» di John C. Hull: futures e coperture, tassi e swap, opzioni e strategie, albero binomiale, processi stocastici, Black-Scholes. Sotto ogni video c'è una scheda con i concetti chiave, un esempio numerico e gli errori da evitare.",
              "A video series, in Italian and English, that starts from the basics and gets to pricing models. It follows the structure of John C. Hull's «Options, Futures and Other Derivatives»: futures and hedging, rates and swaps, options and strategies, binomial trees, stochastic processes, Black-Scholes. Each video comes with a note covering the key concepts, a worked example and the mistakes to avoid."
            )}
          </p>
          <p className="series-intro">
            {L(
              lang,
              "Il livello è intermedio: basta avere familiarità con rendimenti e percentuali. Gli episodi si possono seguire in ordine oppure scegliere per argomento; i numeri dei capitoli citati sono quelli della decima edizione del libro. Le schede sono materiale educativo e non contengono indicazioni su strumenti da comprare o vendere.",
              "The level is intermediate: being comfortable with returns and percentages is enough. Episodes can be watched in order or picked by topic; chapter numbers refer to the book's 10th edition. The notes are educational material and contain no indication of instruments to buy or sell."
            )}
          </p>
          <span className="series-meta deriv-meta">
            {L(lang, `${numbered} episodi pubblicati + ${published.length - numbered} approfondimenti`, `${numbered} episodes published + ${published.length - numbered} deep dives`)}
          </span>
        </header>

        <h2 className="series-group-title">{L(lang, "La serie completa su YouTube", "The full series on YouTube")}</h2>
        <YouTubeEmbed
          playlistId={DERIVATI_PLAYLIST[k]}
          title={L(lang, "Strumenti derivati: playlist completa", "Derivatives: full playlist")}
          onPlay={(id) => trackVideoPlay(id, "derivati_series")}
        />

        {DERIVATI_GROUPS.map((g) => {
          const eps = published.filter((e) => e.group === g.key);
          if (!eps.length) return null;
          return (
            <section key={g.key} className="deriv-group">
              <h2 className="series-group-title">{g[k].title}</h2>
              <p className="deriv-group-intro">{g[k].intro}</p>
              <div className="deriv-list">
                {eps.map((ep) => (
                  <EpisodeCard
                    key={ep.id}
                    ep={ep}
                    c={ep[k]}
                    lang={lang}
                    open={openId === ep.id ? true : undefined}
                    onToggle={(e) => { if (!e.currentTarget.open && openId === ep.id) setOpenId(null); }}
                  />
                ))}
              </div>
            </section>
          );
        })}

        <h2 className="series-group-title">{L(lang, "Da leggere nella Knowledge Base", "Further reading in the Knowledge Base")}</h2>
        <div className="lab-list">
          {KB_READING.map((r) => (
            <div key={r.slug} className="lab-item">
              <Link to={`/${lang}/kb/${r.slug}`}>{L(lang, r.it, r.en)}</Link>
            </div>
          ))}
        </div>

        <p className="series-disclaimer">
          {L(
            lang,
            "Contenuto a scopo educativo e informativo. Non costituisce consulenza in materia di investimenti, raccomandazione né sollecitazione. I derivati sono strumenti complessi e possono comportare perdite anche superiori al capitale investito. Gli esempi numerici sono ipotetici e servono solo a illustrare i concetti.",
            "Educational and informational content. It does not constitute investment advice, a recommendation or a solicitation. Derivatives are complex instruments and can lead to losses exceeding the capital invested. Numerical examples are hypothetical and only illustrate the concepts."
          )}
        </p>
      </div>
    </>
  );
}
